/* CSV/XLSX student import report dialog.
 * Extracted from Students.js (2026-02 refactor). */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { FileArrowUp } from '@phosphor-icons/react';

export default function StudentsImportReportDialog({ open, onOpenChange, result, language }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="students-import-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <FileArrowUp size={22} className="text-[#0052FF]" weight="duotone" />
            {language === 'fr' ? "Rapport d'import" : 'Import report'}
          </DialogTitle>
          <DialogDescription>
            {result?.filename && (
              <span className="text-xs text-[#64748B]">
                {result.filename} · {language === 'fr' ? 'encodage' : 'encoding'}:{' '}
                <b>{result?.encoding}</b> · {language === 'fr' ? 'séparateur' : 'delimiter'}:{' '}
                <b>{result?.delimiter === '\t' ? 'TAB' : result?.delimiter}</b>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="p-3 bg-emerald-50 rounded" data-testid="import-imported-count">
                <div className="font-bold text-emerald-700 text-2xl">{result.imported ?? 0}</div>
                <div className="text-xs text-emerald-800">{language === 'fr' ? 'Importés' : 'Imported'}</div>
              </div>
              <div className="p-3 bg-amber-50 rounded" data-testid="import-skipped-count">
                <div className="font-bold text-amber-700 text-2xl">{result.skipped ?? 0}</div>
                <div className="text-xs text-amber-800">{language === 'fr' ? 'Ignorés' : 'Skipped'}</div>
              </div>
              <div className="p-3 bg-red-50 rounded" data-testid="import-errors-count">
                <div className="font-bold text-red-700 text-2xl">{(result.errors || []).length}</div>
                <div className="text-xs text-red-800">{language === 'fr' ? 'Erreurs' : 'Errors'}</div>
              </div>
            </div>

            {(result.errors || []).length > 0 && (
              <div>
                <h4 className="text-xs uppercase font-bold text-red-700 mb-1">
                  {language === 'fr' ? 'Lignes en erreur' : 'Rows with errors'}
                </h4>
                <div className="max-h-48 overflow-y-auto border border-red-100 rounded bg-red-50/50">
                  <table className="w-full text-xs">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left w-12">{language === 'fr' ? 'Ligne' : 'Row'}</th>
                        <th className="p-1.5 text-left">{language === 'fr' ? 'Raison' : 'Reason'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i} className="border-t border-red-100" data-testid={`import-error-row-${i}`}>
                          <td className="p-1.5 font-mono">{e.row}</td>
                          <td className="p-1.5 text-red-800">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(result.preview || []).length > 0 && (
              <div>
                <h4 className="text-xs uppercase font-bold text-emerald-700 mb-1">
                  {language === 'fr' ? 'Aperçu (20 premiers)' : 'Preview (first 20)'}
                </h4>
                <div className="max-h-48 overflow-y-auto border border-[#E2E8F0] rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F8FAFC] sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left w-12">{language === 'fr' ? 'Ligne' : 'Row'}</th>
                        <th className="p-1.5 text-left">{language === 'fr' ? 'Élève' : 'Student'}</th>
                        <th className="p-1.5 text-left">{language === 'fr' ? 'Classe' : 'Class'}</th>
                        <th className="p-1.5 text-left">{language === 'fr' ? 'Cycle' : 'Cycle'}</th>
                        <th className="p-1.5 text-left">{language === 'fr' ? 'Avertissements' : 'Warnings'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((p, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-1.5 font-mono">{p.row}</td>
                          <td className="p-1.5 font-medium">{p.last_name} {p.first_name}</td>
                          <td className="p-1.5 font-mono">{p.class_id || '—'}</td>
                          <td className="p-1.5">{p.cycle || '—'}</td>
                          <td className="p-1.5 text-amber-700">
                            {(p.warnings || []).map((w, k) => <div key={k}>⚠ {w}</div>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline" onClick={() => onOpenChange(false)}
            data-testid="students-import-close-btn"
          >
            {language === 'fr' ? 'Fermer' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
